/* Gloramous — routine builder, second version (2026-09-16).

   What changed, and why:
   - Several concerns at once (up to three). Dark circles AND breakouts is a
     normal thing to want.
   - Only what is needed. The first version chose morning and evening steps
     separately, so it bought two cleansers and two creams, then added weekly
     treatments on top -- eleven products for one concern. Now every product
     is chosen once and used where it belongs, a single product that treats two
     of the chosen concerns is preferred over two products, and a moisturiser
     is added only when nothing chosen already is one.
   - The shopper controls the price: every product can be swapped for the next
     best one or removed, and the total follows.

   Rules kept from the first version, because they are about not doing harm:
   - "Reacts easily" removes every acid, peel and retinol.
   - At most one strong product (acid, peel or retinol) in a routine.
   - Sunscreen every morning; it is never optional.

   It uses only what the catalogue records: product type and tags. */
(function () {
  'use strict';

  var root = document.querySelector('[data-routine]');
  if (!root) return;
  var poolNode = root.querySelector('[data-rb-pool]');
  var pool = [];
  try { pool = (JSON.parse(poolNode.textContent) || {}).products || []; } catch (e) { return; }
  if (!pool.length) return;
  // Shopify lists collections.all by title; creation order (variant id) is the order the approved preview ranks ties in.
  if (pool.every(function (p) { return typeof p.id === 'number'; })) pool.sort(function (a, b) { return a.id - b.id; });

  var MAX_CONCERNS = 3;
  var CONCERNS = {
    'blemishes': 'Blemishes', 'brightening': 'Brightening', 'anti-aging': 'Anti-aging',
    'hydration': 'Hydration', 'dark-circles': 'Dark circles', 'lifting': 'Lifting',
    'sensitive-skin': 'Sensitive skin'
  };

  var state = { concerns: [], feel: 'balanced', plan: 'essentials', with: '' };
  var removed = {};            // product id -> true
  var swapAt = {};             // slot key -> index into its alternatives

  /* ── product facts ─────────────────────────────────────────────────────── */
  function tags(p) { return (p.g || '').split('|'); }
  function has(p, t) { return tags(p).indexOf(t) !== -1; }
  function face(p) { return has(p, 'Face'); }
  /* Strong means an exfoliating acid, a peel or retinol. "Hyaluronic acid" is
     a humectant, not an exfoliant: the first version matched the word "acid"
     and kept the gentlest serum in the range away from sensitive skin. */
  function strong(p) {
    if (p.y === 'Acid & Peel') return true;
    return /retinol|glycolic|salicylic|lactic|kojic|\bpeel\b|\baha\b/i.test(p.t);
  }
  function covers(p, c) { return c === 'dark-circles' ? p.y === 'Eye Care' : has(p, CONCERNS[c]); }
  function money(c) { return '$' + (c / 100).toFixed(2); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }
  function allowed(p) {
    if (state.feel === 'sensitive' && strong(p)) return false;
    return true;
  }
  function feelScore(p) {
    var n = (p.t + ' ' + p.g).toLowerCase(), s = 0;
    if (state.feel === 'sensitive' && has(p, 'Sensitive skin')) s += 40;
    if (state.feel === 'dry' && (has(p, 'Hydration') || /hydrat|olive|ultra|oil/.test(n))) s += 25;
    if (state.feel === 'oily' && (has(p, 'Blemishes') || /foam|gel|anti-bacterial|zinc/.test(n))) s += 25;
    if (state.with && p.h === state.with) s += 500;
    // Price counts for real: $10 costs 2 points, $60 costs 12. The first pass
    // divided by 10000, so a $60 serum beat a $30 cream for the same concern on
    // a 5-point form preference -- the opposite of an affordable first routine.
    return s - p.p / 500;
  }
  function ranked(list, extra) {
    return list.slice().sort(function (a, b) {
      return (feelScore(b) + (extra ? extra(b) : 0)) - (feelScore(a) + (extra ? extra(a) : 0));
    });
  }
  function concernBonus(p) {
    var s = 0;
    state.concerns.forEach(function (c) { if (covers(p, c)) s += 30; });
    return s;
  }

  /* ── the plan ──────────────────────────────────────────────────────────────
     A slot is one job in the routine; its alternatives are every product that
     can do that job, best first. The chosen product is alternatives[swapAt]. */
  function plan() {
    var slots = [];
    var used = {};
    function pick(key, alts) {
      alts = alts.filter(function (p) { return !used[p.id]; });
      if (!alts.length) return null;
      var i = (swapAt[key] || 0) % alts.length;
      var p = alts[i];
      used[p.id] = true;
      return { key: key, p: p, alts: alts, i: i };
    }

    // 1. cleanse, morning and evening
    var cl = pick('cleanse', ranked(pool.filter(function (p) {
      return face(p) && p.y === 'Cleanser & Toner' && has(p, 'Cleanser') && allowed(p);
    }), concernBonus));
    if (cl) { cl.step = 'Cleanse'; cl.when = ['am', 'pm']; cl.order = 1; slots.push(cl); }

    // 2. tone, only in the complete plan
    if (state.plan === 'complete') {
      var tn = pick('tone', ranked(pool.filter(function (p) {
        return face(p) && has(p, 'Toner') && allowed(p);
      }), concernBonus));
      if (tn) { tn.step = 'Tone'; tn.when = ['am', 'pm']; tn.order = 2; slots.push(tn); }
    }

    // 3. treatments: cover every chosen concern with as few products as possible
    var uncovered = state.concerns.slice();
    var strongUsed = false;
    var guard = 0;
    while (uncovered.length && guard++ < 6) {
      var need = uncovered.slice();
      var cands = pool.filter(function (p) {
        if (!face(p) || !allowed(p) || used[p.id]) return false;
        if (strongUsed && strong(p)) return false;
        if (['Face Serum', 'Face Cream', 'Eye Care', 'Acid & Peel'].indexOf(p.y) === -1) return false;
        if (p.y === 'Acid & Peel' && state.plan !== 'complete') return false;
        return need.some(function (c) { return covers(p, c); });
      });
      if (!cands.length) { uncovered.shift(); continue; }
      // the first uncovered concern leads, so a swap on this slot stays on topic
      var lead = need[0];
      var forLead = cands.filter(function (p) { return covers(p, lead); });
      var pool2 = forLead.length ? forLead : cands;
      var alts = ranked(pool2, function (p) {
        var n = 0;
        need.forEach(function (c) { if (covers(p, c)) n++; });
        return n * 60 + (p.y === 'Face Serum' ? 2 : 0);
      });
      var key = 'treat:' + lead;
      var t = pick(key, alts);
      if (!t) { uncovered.shift(); continue; }
      t.treats = need.filter(function (c) { return covers(t.p, c); });
      if (!t.treats.length) t.treats = [lead];
      uncovered = uncovered.filter(function (c) { return t.treats.indexOf(c) === -1; });
      if (strong(t.p)) {
        strongUsed = true;
        t.step = 'Treat'; t.when = ['pm']; t.freq = 'strong'; t.order = 3;
      } else if (t.p.y === 'Eye Care') {
        t.step = 'Eyes'; t.when = ['am', 'pm']; t.order = 4;
      } else if (/vitamin c/i.test(t.p.t)) {
        t.step = 'Treat'; t.when = ['am']; t.order = 3;
      } else {
        t.step = 'Treat'; t.when = ['am', 'pm']; t.order = t.p.y === 'Face Cream' ? 5 : 3;
      }
      slots.push(t);
    }

    // 4. moisturise, only when nothing chosen is already a cream
    var hasCream = slots.some(function (s) { return s.p.y === 'Face Cream'; });
    var wantCream = !hasCream && (state.plan === 'complete' || state.feel === 'dry' || !state.concerns.length);
    if (wantCream) {
      var mo = pick('moisture', ranked(pool.filter(function (p) {
        return face(p) && p.y === 'Face Cream' && !/sunscreen|spf/i.test(p.t) && allowed(p);
      }), concernBonus));
      if (mo) { mo.step = 'Moisturise'; mo.when = ['am', 'pm']; mo.order = 5; slots.push(mo); }
    }

    // 5. protect, every morning
    var sp = pick('protect', ranked(pool.filter(function (p) {
      return face(p) && p.y === 'Sun Care' && allowed(p);
    })));
    if (sp) { sp.step = 'Protect'; sp.when = ['am']; sp.order = 6; slots.push(sp); }

    // 6. one weekly treatment in the complete plan
    if (state.plan === 'complete') {
      var wk = pick('weekly', ranked(pool.filter(function (p) {
        if (!face(p) || !allowed(p)) return false;
        if (p.y === 'Exfoliator & Scrub' && state.feel === 'sensitive') return false;
        return p.y === 'Face Mask' || p.y === 'Exfoliator & Scrub';
      }), concernBonus));
      if (wk) { wk.step = 'Weekly'; wk.when = ['week']; wk.order = 7; slots.push(wk); }
    }

    slots.sort(function (a, b) { return a.order - b.order; });
    return slots;
  }

  /* ── render ───────────────────────────────────────────────────────────── */
  var listEl = root.querySelector('[data-rb2-list]');
  var amEl = root.querySelector('[data-rb2-am]');
  var pmEl = root.querySelector('[data-rb2-pm]');
  var nEl = root.querySelector('[data-rb2-n]');
  var nounEl = root.querySelector('[data-rb2-noun]');
  var totalEl = root.querySelector('[data-rb2-total]');
  var addBtn = root.querySelector('[data-rb2-add]');
  var current = [];

  var WHEN = { am: 'Morning', pm: 'Evening', week: 'Once a week' };
  var LABEL = {};
  root.querySelectorAll('[data-rb2-concern]').forEach(function (b) {
    LABEL[b.getAttribute('data-rb2-concern')] = b.textContent.trim();
  });

  function render() {
    current = plan();
    var html = '', total = 0, count = 0, am = [], pm = [];
    current.forEach(function (s) {
      var off = !!removed[s.p.id];
      if (!off) { total += s.p.p; count++; }
      var img = s.p.i ? '<img src="' + esc(s.p.i) + '" alt="" width="80" height="80" loading="lazy">' : '';
      var pills = s.when.map(function (w) {
        return '<span class="rb2-pill rb2-pill--' + w + '">' + WHEN[w] + '</span>';
      }).join('');
      if (s.freq === 'strong') pills += '<span class="rb2-pill rb2-pill--note">2–3 evenings a week</span>';
      var treats = s.treats ? '<span class="rb2-treats">' + s.treats.map(function (c) {
        return '<span>' + esc(LABEL[c] || c) + '</span>';
      }).join('') + '</span>' : '';
      var size = s.p.s && s.p.s !== 'Default Title' ? '<span class="rb2-size">' + esc(s.p.s) + '</span>' : '';
      html += '<li class="rb2-item' + (off ? ' is-off' : '') + '" data-slot="' + esc(s.key) + '">' +
        '<a class="rb2-img" href="' + esc(s.p.u) + '" tabindex="-1" aria-hidden="true">' + img + '</a>' +
        '<div class="rb2-body"><span class="rb2-step">' + s.step + '</span>' +
        '<a class="rb2-name" href="' + esc(s.p.u) + '">' + esc(s.p.t) + '</a>' +
        '<span class="rb2-meta"><b>' + money(s.p.p) + '</b>' + size + '</span>' + treats +
        '<span class="rb2-pills">' + pills + '</span></div>' +
        '<div class="rb2-actions">' +
          (s.alts.length > 1 && !off ? '<button type="button" class="rb2-act" data-rb2-swap="' + esc(s.key) + '">Swap</button>' : '') +
          '<button type="button" class="rb2-act" data-rb2-toggle="' + esc(s.p.id) + '">' + (off ? 'Put back' : 'Remove') + '</button>' +
        '</div></li>';
      if (!off) {
        if (s.when.indexOf('am') > -1) am.push(s);
        if (s.when.indexOf('pm') > -1) pm.push(s);
      }
    });
    listEl.innerHTML = html;
    var step = function (s) {
      return '<li><span class="rb2-how-step">' + s.step + '</span><span class="rb2-how-name">' + esc(s.p.t) + '</span></li>';
    };
    amEl.innerHTML = am.map(step).join('');
    pmEl.innerHTML = pm.map(step).join('');
    if (window.GloramousRoll) {
      window.GloramousRoll(totalEl, money(total));
      window.GloramousRoll(nEl, String(count));
    } else { totalEl.textContent = money(total); nEl.textContent = count; }
    nounEl.textContent = count === 1 ? 'product' : 'products';
    addBtn.disabled = !count;
    syncUrl();
    // remember the answers, so the catalogue and product pages can say what suits this skin
    // (saved even with no concerns, so clearing them here is remembered as well)
    if (window.GloramousSkin) {
      window.GloramousSkin.set({ concerns: state.concerns, feel: state.feel, plan: state.plan });
    }
  }

  /* ── controls ─────────────────────────────────────────────────────────── */
  var concernBtns = [].slice.call(root.querySelectorAll('[data-rb2-concern]'));
  var countEl = root.querySelector('[data-rb2-count]');
  function paintControls() {
    concernBtns.forEach(function (b) {
      var on = state.concerns.indexOf(b.getAttribute('data-rb2-concern')) > -1;
      b.setAttribute('aria-pressed', String(on));
      b.disabled = !on && state.concerns.length >= MAX_CONCERNS;
    });
    if (countEl) countEl.textContent = state.concerns.length
      ? state.concerns.length + ' of ' + MAX_CONCERNS + ' chosen' : 'Pick up to ' + MAX_CONCERNS;
    root.querySelectorAll('[data-rb2-feel]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-rb2-feel') === state.feel));
    });
    root.querySelectorAll('[data-rb2-plan]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-rb2-plan') === state.plan));
    });
    var warn = root.querySelector('[data-rb2-sensitive]');
    if (warn) warn.hidden = state.feel !== 'sensitive';
  }
  function changed() { swapAt = {}; removed = {}; paintControls(); render(); }

  root.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest('button') : null;
    if (!b || !root.contains(b)) return;
    if (b.hasAttribute('data-rb2-concern')) {
      var c = b.getAttribute('data-rb2-concern');
      var at = state.concerns.indexOf(c);
      if (at > -1) state.concerns.splice(at, 1);
      else if (state.concerns.length < MAX_CONCERNS) state.concerns.push(c);
      changed();
    } else if (b.hasAttribute('data-rb2-feel')) {
      state.feel = b.getAttribute('data-rb2-feel'); changed();
    } else if (b.hasAttribute('data-rb2-plan')) {
      state.plan = b.getAttribute('data-rb2-plan'); changed();
    } else if (b.hasAttribute('data-rb2-swap')) {
      var key = b.getAttribute('data-rb2-swap');
      swapAt[key] = (swapAt[key] || 0) + 1;
      render();
      var again = root.querySelector('[data-rb2-swap="' + key + '"]');
      if (again) again.focus({ preventScroll: true });
    } else if (b.hasAttribute('data-rb2-toggle')) {
      var id = b.getAttribute('data-rb2-toggle');
      removed[id] = !removed[id];
      render();
      var t = root.querySelector('[data-rb2-toggle="' + id + '"]');
      if (t) t.focus({ preventScroll: true });
    }
  });

  /* ── add everything that is not removed ───────────────────────────────── */
  addBtn.addEventListener('click', function () {
    var lines = current.filter(function (s) { return !removed[s.p.id]; }).map(function (s) {
      return { id: s.p.id, title: s.p.t, price: s.p.p, image: s.p.i,
               variant: s.p.s === 'Default Title' ? '' : s.p.s };
    });
    if (!lines.length) return;
    var label = addBtn.textContent;
    addBtn.disabled = true;
    addBtn.textContent = 'Adding…';
    var done = function () {
      addBtn.textContent = 'Added';
      setTimeout(function () { addBtn.disabled = false; addBtn.textContent = label; }, 1600);
    };
    if (window.GLORAMOUS_PREVIEW) {
      var r = window.GloramousCart && window.GloramousCart.addMany(lines);
      (r && r.then ? r : Promise.resolve()).then(done);
      return;
    }
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: lines.map(function (l) { return { id: l.id, quantity: 1 }; }) })
    }).then(function (res) {
      if (!res.ok) throw new Error('add failed');
      return window.GloramousCart && window.GloramousCart.open(lines.length + ' products added');
    }).then(done, function () {
      /* say so: before, a failed add (one product sold out) just reset the button */
      addBtn.textContent = (window.GloramousT && window.GloramousT('Could not add that. Try again.')) || 'Could not add that. Try again.';
      setTimeout(function () { addBtn.disabled = false; addBtn.textContent = label; }, 2600);
    });
  });

  /* ── the address bar carries the answers, so a routine can be shared ────── */
  function syncUrl() {
    try {
      var q = new URLSearchParams();
      if (state.concerns.length) q.set('concern', state.concerns.join(','));
      if (state.feel !== 'balanced') q.set('feel', state.feel);
      if (state.plan !== 'essentials') q.set('plan', state.plan);
      if (state.with) q.set('with', state.with);
      var qs = q.toString();
      history.replaceState(null, '', location.pathname + (qs ? '?' + qs : ''));
    } catch (e) {}
  }

  /* ── arriving with answers: the quiz, the homepage chips, a product page ── */
  try {
    var params = new URLSearchParams(location.search);
    var cs = [];
    params.getAll('concern').forEach(function (v) {
      v.split(',').forEach(function (x) {
        x = x.trim().toLowerCase();
        if (CONCERNS[x] && cs.indexOf(x) === -1 && cs.length < MAX_CONCERNS) cs.push(x);
      });
    });
    state.concerns = cs;
    var f = params.get('feel');
    if (['dry', 'balanced', 'oily', 'sensitive'].indexOf(f) > -1) state.feel = f;
    var pl = params.get('plan') || params.get('length');
    if (pl === 'complete' || pl === 'full') state.plan = 'complete';
    var pin = params.get('with');
    if (pin && /^[a-z0-9-]+$/.test(pin)) state.with = pin;
    // opened with no answers in the address (tab bar, menu, a later visit):
    // start from what the quiz or the last visit to this page saved
    if (!params.has('concern') && !params.has('feel')) {
      var saved = JSON.parse(localStorage.getItem('gloramous.skin') || 'null');
      if (saved) {
        (saved.concerns || []).forEach(function (x) {
          if (CONCERNS[x] && cs.indexOf(x) === -1 && cs.length < MAX_CONCERNS) cs.push(x);
        });
        state.concerns = cs;
        if (['dry', 'balanced', 'oily', 'sensitive'].indexOf(saved.feel) > -1) state.feel = saved.feel;
        if (!pl && saved.plan === 'complete') state.plan = 'complete';
      }
    }
  } catch (e) {}

  /* ── save my routine as an image ─────────────────────────────────────────
     A picture of the routine to keep on the phone or send on WhatsApp: the
     steps, morning and evening, with the total. Drawn on a canvas; product
     photos are included when the browser allows it, and left out otherwise. */
  (function () {
    var host = root.querySelector('.rb2-howto');
    if (!host || root.querySelector('[data-rb2-save]')) return;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn--ghost rb2-save';
    btn.setAttribute('data-rb2-save', '');
    btn.textContent = 'Save my routine as an image';
    host.insertAdjacentElement('afterend', btn);
    var T = function (s) { return window.GloramousT ? (window.GloramousT(s) || s) : s; };

    function loadImg(src) {
      return new Promise(function (res) {
        if (!src) { res(null); return; }
        var im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = function () { res(im); };
        im.onerror = function () { res(null); };
        im.src = src;
      });
    }
    btn.addEventListener('click', function () {
      var items = current.filter(function (s) { return !removed[s.p.id]; });
      if (!items.length) return;
      btn.disabled = true;
      Promise.all(items.map(function (s) { return loadImg(s.p.i); })).then(function (imgs) {
        var W = 1080, rowH = 150, H = 420 + items.length * rowH + 140;
        var c = document.createElement('canvas');
        c.width = W; c.height = H;
        var x = c.getContext('2d');
        var rtl = document.documentElement.dir === 'rtl';
        x.fillStyle = '#E9EFED'; x.fillRect(0, 0, W, H);
        x.fillStyle = '#102012'; x.fillRect(0, 0, W, 250);
        x.fillStyle = '#E9CA8E'; x.font = '600 34px sans-serif'; x.fillText('Gloramous', 70, 90);
        x.fillStyle = '#F4F6F1'; x.font = '700 64px sans-serif';
        x.textAlign = rtl ? 'right' : 'left';
        var X0 = rtl ? W - 70 : 70;
        x.fillText(T('Your routine'), X0, 180);
        x.font = '500 30px sans-serif'; x.fillStyle = '#C9D5CB';
        var labels = state.concerns.map(function (c) { return T(LABEL[c] || c); }).join(' · ');
        x.fillText(labels, X0, 228);
        var y = 300;
        items.forEach(function (s, i) {
          x.fillStyle = '#FFFFFF';
          x.fillRect(50, y, W - 100, rowH - 20);
          var im = imgs[i];
          var ix = rtl ? W - 70 - 110 : 70;
          if (im) {
            try { x.drawImage(im, ix, y + 10, 110, 110); } catch (e) {}
          }
          var tx = rtl ? W - 200 : 200;
          x.textAlign = rtl ? 'right' : 'left';
          x.fillStyle = '#0C623D'; x.font = '600 26px sans-serif';
          x.fillText(T(s.step) + ' · ' + s.when.map(function (w) { return T(WHEN[w]); }).join(' + '), tx, y + 50);
          x.fillStyle = '#121514'; x.font = '600 34px sans-serif';
          x.fillText(s.p.t, tx, y + 95);
          x.textAlign = rtl ? 'left' : 'right';
          x.font = '600 30px sans-serif';
          x.fillText(money(s.p.p), rtl ? 90 : W - 90, y + 95);
          y += rowH;
        });
        var total = items.reduce(function (a, s) { return a + s.p.p; }, 0);
        x.textAlign = rtl ? 'left' : 'right';
        x.fillStyle = '#121514'; x.font = '700 44px sans-serif';
        x.fillText(money(total), rtl ? 90 : W - 90, y + 60);
        x.textAlign = rtl ? 'right' : 'left';
        x.font = '500 26px sans-serif'; x.fillStyle = '#46514C';
        x.fillText(location.host || 'gloramous', X0, y + 60);
        var finish = function (blob) {
          btn.disabled = false;
          if (!blob) return;
          var file = new File([blob], 'my-gloramous-routine.png', { type: 'image/png' });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            navigator.share({ files: [file], title: T('Your routine') }).catch(function () {});
            return;
          }
          var a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = 'my-gloramous-routine.png';
          document.body.appendChild(a);
          a.click();
          setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
        };
        try { c.toBlob(finish, 'image/png'); }
        catch (e) {
          // a product photo from another origin tainted the canvas: draw it again without photos
          imgs = imgs.map(function () { return null; });
          btn.disabled = false;
        }
      });
    });
  })();

  root.classList.add('is-ready');
  paintControls();
  render();
})();
